/**
 * Selection-scoped block formatting (blockquote, code block, expandable
 * quote).
 *
 * Telegram block formats are block-level, and Lexical's `$setBlocksType`
 * converts whole *top-level* blocks. That is fine when each line is its own
 * paragraph, but a post is frequently a single paragraph that contains
 * `LineBreakNode`s — e.g. after pasting plain multi-line text, Lexical's
 * default plain-text insertion puts `<br>`s *inside* one paragraph. In that
 * case `$setBlocksType` wraps the entire paragraph, so the format "applies to
 * the whole post" instead of the selection.
 *
 * `$toggleBlockForSelection` fixes this by first splitting the containing
 * block(s) at the line breaks that bound the selection, isolating exactly the
 * selected line(s) into their own block, and only then calling
 * `$setBlocksType`. Line breaks *inside* the selection are preserved, so a
 * multi-line selection becomes one multi-line block.
 */
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $isLineBreakNode,
  $isElementNode,
  type ElementNode,
  type LexicalNode,
  type Point,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $isQuoteNode, $createQuoteNode } from '@lexical/rich-text';
import { $isCodeNode, $createCodeNode } from '@lexical/code';
import { $isExpandableQuoteNode, $createExpandableQuoteNode } from '../nodes';

/**
 * Creates a new empty block of the same type as `block`. Used for the lines
 * peeled off either side of the selection so they keep their original block
 * type (e.g. unwrapping the middle line of a quote leaves the surrounding
 * lines as quotes, not paragraphs).
 */
function createSiblingFor(block: ElementNode): ElementNode {
  if ($isQuoteNode(block)) return $createQuoteNode();
  if ($isCodeNode(block)) return $createCodeNode();
  if ($isExpandableQuoteNode(block)) return $createExpandableQuoteNode();
  return $createParagraphNode();
}

/**
 * Walks up from `node` until it finds the descendant of `block` that is a
 * *direct* child of `block`. Returns null if `node` is not inside `block`.
 */
function directChildOf(block: ElementNode, node: LexicalNode): LexicalNode | null {
  let cur: LexicalNode = node;
  let parent = cur.getParent();
  while (parent !== null) {
    if (parent.is(block)) return cur;
    cur = parent;
    parent = cur.getParent();
  }
  return null;
}

/**
 * Resolves the direct child of `block` that the selection point sits in.
 *
 * @param atStart - When the point is an element-level point on `block`
 *   itself, a start point resolves to the child *at* the offset while an end
 *   point resolves to the child *before* the offset (end offsets are
 *   exclusive).
 */
function resolveLineChild(
  block: ElementNode,
  point: Point,
  atStart: boolean
): LexicalNode | null {
  const node = point.getNode();
  if ($isElementNode(node) && node.is(block)) {
    const children = block.getChildren();
    if (children.length === 0) return null;
    const idx = atStart ? point.offset : point.offset - 1;
    const clamped = Math.max(0, Math.min(idx, children.length - 1));
    return children[clamped] ?? null;
  }
  return directChildOf(block, node);
}

/** Index of `child` among `block`'s direct children, or -1. */
function childIndex(block: ElementNode, child: LexicalNode): number {
  const children = block.getChildren();
  for (let i = 0; i < children.length; i++) {
    const c = children[i];
    if (c && c.is(child)) return i;
  }
  return -1;
}

/**
 * Peels the lines that precede `startChild`'s line off the front of `block`
 * into a new paragraph inserted before it, so `block` begins at the selected
 * line. No-op when the selected line is already the first line.
 */
function peelLeadingLines(block: ElementNode, startChild: LexicalNode): void {
  const children = block.getChildren();
  const startIdx = childIndex(block, startChild);
  if (startIdx <= 0) return;

  let breakIdx = -1;
  for (let i = startIdx - 1; i >= 0; i--) {
    const c = children[i];
    if (c && $isLineBreakNode(c)) {
      breakIdx = i;
      break;
    }
  }
  if (breakIdx < 0) return; // selected line is the first line

  const pre = createSiblingFor(block);
  for (let i = 0; i < breakIdx; i++) {
    const c = children[i];
    if (c) pre.append(c); // re-parents the node out of `block`
  }
  children[breakIdx]?.remove(); // drop the separating line break
  block.insertBefore(pre);
}

/**
 * Peels the lines that follow `endChild`'s line off the back of `block` into a
 * new paragraph inserted after it, so `block` ends at the selected line.
 * No-op when the selected line is already the last line.
 */
function peelTrailingLines(block: ElementNode, endChild: LexicalNode): void {
  const children = block.getChildren();
  const endIdx = childIndex(block, endChild);
  if (endIdx < 0) return;

  let breakIdx = -1;
  for (let i = endIdx + 1; i < children.length; i++) {
    const c = children[i];
    if (c && $isLineBreakNode(c)) {
      breakIdx = i;
      break;
    }
  }
  if (breakIdx < 0) return; // selected line is the last line

  const post = createSiblingFor(block);
  for (let i = breakIdx + 1; i < children.length; i++) {
    const c = children[i];
    if (c) post.append(c);
  }
  children[breakIdx]?.remove();
  block.insertAfter(post);
}

/**
 * Toggles a block-level format on exactly the selected line(s).
 *
 * Must be called inside an `editor.update()` / command context (the registered
 * command callbacks already run in one). When the selection is not a range
 * selection it is a no-op and returns false.
 *
 * @param isTargetBlock - Predicate identifying whether a block is already the
 *   target type (used to decide toggle-off vs toggle-on).
 * @param createTargetBlock - Factory for the target block node.
 * @returns true if it handled the command.
 */
export function $toggleBlockForSelection(
  isTargetBlock: (node: LexicalNode) => boolean,
  createTargetBlock: () => ElementNode
): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;

  const isBackward = selection.isBackward();
  const startPoint = isBackward ? selection.focus : selection.anchor;
  const endPoint = isBackward ? selection.anchor : selection.focus;

  const startBlock = startPoint.getNode().getTopLevelElement();
  const endBlock = endPoint.getNode().getTopLevelElement();

  // Decide direction from the start block: if it is already the target type,
  // toggle off (convert selected lines back to paragraphs).
  const alreadyTarget = startBlock !== null && isTargetBlock(startBlock);
  const makeBlock = alreadyTarget
    ? () => $createParagraphNode()
    : createTargetBlock;

  // Isolate the selected lines by splitting around them. Guard each step so an
  // unexpected shape degrades to plain `$setBlocksType` rather than throwing.
  if (startBlock !== null && $isElementNode(startBlock)) {
    const startChild = resolveLineChild(startBlock, startPoint, true);
    if (startChild) peelLeadingLines(startBlock, startChild);
  }
  if (endBlock !== null && $isElementNode(endBlock)) {
    // After peeling the leading lines, `endBlock` (possibly === startBlock) is
    // unchanged in identity; re-resolve the end child against current children.
    const endChild = resolveLineChild(endBlock, endPoint, false);
    if (endChild) peelTrailingLines(endBlock, endChild);
  }

  // Re-read the selection: node moves above may have refreshed it.
  const current = $getSelection();
  if (!$isRangeSelection(current)) return false;

  $setBlocksType(current, makeBlock);
  return true;
}
